import axios from "axios";
import { enqueue, shouldSkipQueue, flush, subscribe } from "@/lib/offlineQueue";
import { getToken } from "@/lib/session";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- Simple in-memory GET cache -------------------------------------------
// Route transitions feel slow when every page refetches its data from scratch.
// This lightweight cache serves a very recent GET response synchronously so
// navigating back to a page returns instantly and then revalidates in the
// background.
//
//   * Only successful GETs (2xx) are cached.
//   * TTL defaults to 20s and can be overridden via `config.cacheTtl` (ms).
//   * Callers can opt out with `config.cache = false`.
//   * Cache is cleared on any mutation (POST/PATCH/PUT/DELETE) to the same
//     resource root so writes don't serve stale reads.
const _cache = new Map(); // key -> { at: ms, ttl: ms, data: any }
const _cacheKey = (cfg) => {
  const u = cfg.url || "";
  const p = cfg.params ? JSON.stringify(cfg.params) : "";
  return `${u}?${p}`;
};
const _resourceRoot = (url) => (url || "").split("?")[0].split("/").slice(0, 3).join("/");

// Expose for tests / manual eviction if needed.
export const _apiCache = _cache;
export const clearApiCache = () => _cache.clear();

// Inject Authorization header from the session helper as fallback for
// cross-domain cookie issues. Reads via `getToken()` which validates the
// value against a strict regex — a malformed / hostile token is refused.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Serve from cache when possible (adapter shortcut).
  const method = (config.method || "get").toLowerCase();
  if (method === "get" && config.cache !== false) {
    const key = _cacheKey(config);
    const hit = _cache.get(key);
    const ttl = config.cacheTtl ?? 20_000;
    if (hit && Date.now() - hit.at < (hit.ttl ?? ttl)) {
      // Return synthetic response via an adapter so axios still emits a proper
      // response object to the caller.
      config.adapter = () => Promise.resolve({
        status: 200,
        statusText: "OK (cache)",
        data: hit.data,
        headers: { "x-pb-cache": "hit" },
        config,
        request: null,
      });
    }
  }
  return config;
});

// ---- Offline queue interception ----
// Detect "no network" failures (browser offline OR network error w/ no response)
// and stash the mutation in IndexedDB. The auto-flush below replays them when
// connectivity returns.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config || {};
    const isNetworkError = !err?.response && (err?.code === "ERR_NETWORK" || !navigator.onLine);
    const isReplay = cfg.headers?.["x-pb-replay"] === "1";
    if (isNetworkError && !isReplay && !shouldSkipQueue(cfg.method, cfg.url)) {
      try {
        await enqueue({
          method: (cfg.method || "POST").toUpperCase(),
          url: cfg.url,
          data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : null,
          headers: { "Content-Type": "application/json" },
        });
        // Resolve with a synthetic 202 — callers that don't care about the
        // response body (toast on success, refresh on close) still feel OK.
        return Promise.resolve({
          status: 202,
          statusText: "Queued offline",
          data: { queued: true, offline: true },
          headers: {},
          config: cfg,
          request: null,
        });
      } catch (qerr) {
        console.error("Offline queue enqueue failed:", qerr);
      }
    }
    return Promise.reject(err);
  },
);

// ---- Online listener: auto-flush ----
if (typeof window !== "undefined") {
  const tryFlush = () => { if (navigator.onLine) flush(api); };
  window.addEventListener("online", tryFlush);
  // Also try a flush whenever the queue is mutated (e.g. a new item was enqueued
  // by another tab); the subscribe call returns an unsubscribe we intentionally
  // ignore for the lifetime of the page.
  subscribe(() => { if (navigator.onLine) flush(api); });
  // First-load: if we boot online with leftover queue from a prior session, drain.
  if (navigator.onLine) setTimeout(tryFlush, 1500);
}

export const wsUrl = (path) => {
  const u = new URL(BACKEND_URL);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}${path}`;
};
